import React, { useState } from 'react';
import { Box, Flex, Text, HStack } from "@chakra-ui/react";
import { Link } from "@reach/router";
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { NewUser } from '../../types/User.type';
import { LoginPath, ReferralToken } from '../Login';
import { LoginPathButton } from '../Login/components/LoginPathButton';
import UsernamePassword, { createPasswordSchema, passwordConfirmationSchema } from '../Login/components/UsernamePassword';
import { FormikInputWithLabel } from 'newui/components/ui/InputWithLabel';
import { Button } from 'newui/components/Button';
import { FormikAcceptTermsCheckbox } from '../Login/FormikAcceptTermsCheckbox';
import { navigate } from '@reach/router';
import { createGuestUserAccount } from '../../auth-api';
import { getOrFetchXsrfToken } from '../../utils';
import { errorMessages } from '../Login/constants/ErrorMessages';
import { getErrorMessage } from 'api-v2';
import { FlashError } from 'components/FormInputs/FlashError';
import { useAuthContext } from '../../context/AuthContextProvider';
import { LOCAL_STORAGE_KEYS } from 'newui/constants/localStorageKeys';
import AuthContainer from '../AuthContainer';

interface GuestSignupProps {
	referralToken: ReferralToken;
}

interface SubmitError {
	heading: string;
	message: string;
}

// Validation schema with Yup
const validationSchema = Yup.object({
	first_name: Yup.string()
		.trim()
		.matches(/^[a-zA-Z\s'-]+$/, "First name is not valid")
		.min(1, "First name should be at least 1 character")
		.max(50, "First name should not exceed 50 characters")
		.required('First name is required'),
	last_name: Yup.string()
		.trim()
		.matches(/^[a-zA-Z\s'-]+$/, "Last name is not valid")
		.min(1, "Last name should be at least 1 character")
		.max(50, "Last name should not exceed 50 characters")
		.required('Last name is required'),
	email: Yup.string()
		.email('Invalid email address')
		.max(190, "Email should not exceed 190 characters")
		.required('Email is required'),
	password: createPasswordSchema,
	password_confirmation: passwordConfirmationSchema,
	acceptTerms: Yup.boolean()
		.oneOf([true], 'You must accept the terms and conditions')
		.required('You must accept the terms and conditions'),
});

const GuestSignup: React.FC<GuestSignupProps> = ({ referralToken }) => {
	const [submitError, setSubmitError] = useState<SubmitError | null>(null);
	const { authenticateUser } = useAuthContext();

	const initialValues: NewUser = {
		first_name: '',
		last_name: '',
		email: '',
		password: '',
		password_confirmation: '',
		acceptTerms: false,
		token: referralToken.tokenValue,
		token_type: referralToken.tokenType
	};

	const handleSubmit = async (
		values: NewUser,
		{ setSubmitting }: FormikHelpers<NewUser>
	): Promise<void> => {
		setSubmitError(null);

		try {
			await getOrFetchXsrfToken();
			const userProfile = await createGuestUserAccount(values);

			if (userProfile?.data?.success) {
				await authenticateUser(userProfile.data);

				// Navigate to the stored redirect URL if available, otherwise
				// the redirect handler effect will pick it up on re-render.
				const redirectUrl = localStorage.getItem(LOCAL_STORAGE_KEYS.REDIRECT_PATH);
				if (redirectUrl) {
					localStorage.removeItem(LOCAL_STORAGE_KEYS.REDIRECT_PATH);
					navigate(redirectUrl);
				}
			}

		} catch (e) {

			const status = e.response?.status;
			let errorMessage = errorMessages[status]?.description;

			if (e.response) {
				try {
					errorMessage = getErrorMessage(e);
				} catch {
					errorMessage = "Failed to parse error response.";
				}
			} else {
				errorMessage = e.message ?? errorMessage;
			}

			setSubmitError({
				heading: 'Signup Failed',
				message: errorMessage
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<AuthContainer additionalHeadingText="Create your account to get started.">
			{submitError && (
				<FlashError
					heading={submitError.heading}
					text={submitError.message}
					margin="0px 0px 20px 0px"
				/>
			)}

			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ errors, touched, isSubmitting, setFieldValue }) => (
					<Form>
						<Flex gap={4} direction={{ base: 'column', sm: 'row' }}>
							<FormikInputWithLabel data-testid="signup-firstname" label="First Name" name='first_name' setFieldValue={setFieldValue} placeholder='Your First Name' flexGrow={1} maxLength={50} />

							<FormikInputWithLabel data-testid="signup-lastname" label="Last Name" name='last_name' setFieldValue={setFieldValue} placeholder='Your Last Name' flexGrow={1} maxLength={50} />
						</Flex>

						<UsernamePassword emailDataTestid='signup-email' isSignUp passwordConfirmationDataTestid='password-confirmation-signup' passwordDataTestid='password-signup' setFieldValue={setFieldValue} />

						<Flex gap={0} paddingBottom={6} direction={"row"}>
							<HStack>
								<FormikAcceptTermsCheckbox name='acceptTerms'>
									<Text fontSize="sm">
										I accept the{' '}
										<Link to="/terms" className="text-blue-500 underline">
											Terms and Conditions
										</Link>
										{' '}and{' '}
										<Link to="/privacy" className="text-blue-500 underline">
											Privacy Policy
										</Link>
									</Text>
								</FormikAcceptTermsCheckbox>
							</HStack>
						</Flex>

						<Button
							type="submit"
							width="100%"
							isLoading={isSubmitting}
							loadingText="Creating Account..."
							data-testid="signup-button"
						>
							Create Account
						</Button>
					</Form>
				)}
			</Formik>

			<LoginPathButton loginPath={LoginPath.Login} showReferralText />
		</AuthContainer>
	);
};

export default GuestSignup;